/* =========================
   ProductDetails.jsx
   - Phosphor icons only
   - Details supports:
     1) array [{key,value}] ✅ (new)
     2) html string ✅ (old fallback)
========================= */

import React, { useMemo } from "react"
import { motion } from "framer-motion"
import { X, ImageSquare, Tag, Package, CheckCircle, XCircle, FilePdf } from "phosphor-react"

export default function ProductDetails({ product, onClose }) {
  // ✅ hooks first (never conditional)
  const computedDetails = useMemo(() => {
    const raw = product?.details

    if (Array.isArray(raw)) return { type: "array", value: raw }
    if (typeof raw === "string" && raw.trim()) return { type: "html", value: raw }
    return { type: "none", value: null }
  }, [product?.details])

  if (!product) return null

  const images = Array.isArray(product.images) ? product.images : []
  const variants = Array.isArray(product.variants) ? product.variants : []

  const money = (v) => {
    const n = Number.parseFloat(v)
    if (Number.isNaN(n)) return "-"
    return `$${n.toFixed(2)}`
  }

  return (
    <>
      {/* overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/60"
      />

      {/* panel */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.25 }}
        className="fixed inset-y-0 right-0 z-50 w-full md:w-2/5 bg-white border-l border-neutral-200/70 shadow-2xl overflow-y-auto text-start"
      >
        {/* header */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-neutral-200/70">
          <div className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-2xl bg-neutral-900 text-white flex items-center justify-center">
                <Package size={18} weight="bold" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-neutral-900 truncate">Product Details</h2>
                <p className="text-xs text-neutral-500 truncate">{product.name || "—"}</p>
              </div>
            </div>

            <button onClick={onClose} className="p-2 rounded-xl hover:bg-neutral-100 text-neutral-700 transition" aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-5 bg-neutral-50/60 space-y-5">
          {/* images */}
          <Section icon={<ImageSquare size={16} />} title="Images">
            {images.length ? (
              <div className="grid grid-cols-3 gap-3">
                {images.map((img, idx) => (
                  <div key={idx} className="rounded-2xl overflow-hidden border border-neutral-200 bg-white">
                    <img
                      src={img || "/placeholder.svg"}
                      alt={`${product.name || "product"}-${idx + 1}`}
                      className="h-24 w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No images added." />
            )}
          </Section>

  

          {/* basic */}
          <Section title="Basic Information">
            <InfoGrid
              items={[
                { label: "Name", value: product.name || "-" },
                { label: "Reference", value: product.reference || "-" },
                { label: "SKU", value: product.sku || "-" },
                { label: "Description", value: product.description || "-" },
              ]}
            />
          </Section>

          {/* pricing */}
          <Section title="Pricing & Stock">
            <div className="grid grid-cols-3 gap-3">
              <Metric label="Price" value={money(product.price)} />
              <Metric label="Compare" value={product.compare_price ? money(product.compare_price) : "-"} />
              <Metric label="Stock" value={String(product.stock ?? "-")} />
            </div>
          </Section>

          {/* categories */}
          <Section icon={<Tag size={16} />} title="Categories">
            <div className="grid grid-cols-3 gap-3">
              <Metric label="Category" value={product.category?.name || "-"} />
              <Metric label="Subcategory" value={product.subcategory?.name || "-"} />
              <Metric label="Brand" value={product.brand?.name || "-"} />
            </div>
          </Section>

          {/* ✅ details */}
          <Section title="Details">
            {computedDetails.type === "array" ? (
              computedDetails.value?.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {computedDetails.value.map((d, idx) => (
                    <div key={idx} className="rounded-2xl border border-neutral-200 bg-white p-4">
                      <div className="text-xs text-neutral-500">{d?.key || "-"}</div>
                      <div className="text-sm text-neutral-900 mt-1 whitespace-pre-wrap break-words">{d?.value || "-"}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="No details." />
              )
            ) : computedDetails.type === "html" ? (
              <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: computedDetails.value }} />
              </div>
            ) : (
              <EmptyState text="No details." />
            )}
          </Section>

          {/* variants */}
          <Section title="Variants">
            {variants.length ? (
              <div className="space-y-3">
                {variants.map((v) => (
                  <div key={v.id} className="rounded-2xl border border-neutral-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-neutral-900 truncate">{v.name || "-"}</div>
                        <div className="text-xs text-neutral-500 mt-1">SKU: {v.sku || "-"}</div>
                      </div>

                      <div className="flex gap-3">
                        <Chip label="Price" value={money(v.price)} />
                        <Chip label="Stock" value={String(v.stock ?? "-")} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No variants." />
            )}
          </Section>

          {/* status */}
          <Section title="Status">
            <div className="grid grid-cols-2 gap-3">
              <StatusPill label="Active" value={!!product.is_active} />
              <StatusPill label="Top Seller" value={!!product.is_top_seller} />
              <StatusPill label="Featured" value={!!product.is_featured} />
              <StatusPill label="Special Offer" value={!!product.is_special_offer} />
            </div>
          </Section>
        </div>
      </motion.div>
    </>
  )
}

/* ---------- helpers ---------- */

function Section({ title, icon, children }) {
  return (
    <div className="rounded-2xl border border-neutral-200/70 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        {icon ? (
          <div className="h-8 w-8 rounded-xl bg-neutral-900 text-white flex items-center justify-center">{icon}</div>
        ) : null}
        <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
      </div>
      {children}
    </div>
  )
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-center">
      <p className="text-sm text-neutral-600">{text}</p>
    </div>
  )
}

function Metric({ label, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="text-sm font-semibold text-neutral-900 mt-1 truncate">{value}</div>
    </div>
  )
}

function Chip({ label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2">
      <div className="text-[10px] text-neutral-500 leading-none">{label}</div>
      <div className="text-xs font-semibold text-neutral-900 mt-1">{value}</div>
    </div>
  )
}

function StatusPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-3 flex items-center justify-between">
      <div className="text-sm text-neutral-900">{label}</div>
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
          value
            ? "bg-green-500/10 text-green-700 border border-green-500/20"
            : "bg-neutral-100 text-neutral-600 border border-neutral-200"
        }`}
      >
        {value ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} />}
        {value ? "Yes" : "No"}
      </div>
    </div>
  )
}

function InfoGrid({ items }) {
  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div key={idx} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3">
          <div className="text-xs text-neutral-500">{it.label}</div>
          <div className="text-sm text-neutral-900 mt-1 whitespace-pre-wrap">{it.value}</div>
        </div>
      ))}
    </div>
  )
}
